// src/context/AuthContext.tsx
"use client"

import React, {createContext, useContext, useState, useEffect} from "react"
import {useRouter} from "next/navigation"
import {JwtResponseDto, MeApi, MeResponseDto} from "@/api/generated";
import {getApiConfig, getUnauthenticatedApiConfig} from "@/api/api";
import Logger from "@/utils/logger";
import {AxiosError} from "axios";


type AuthContextType = {
    user: MeResponseDto | null
    login: (email: string, password: string) => Promise<JwtResponseDto | undefined>
    logout: () => void,
    isAuthLoading: boolean
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({children}: { children: React.ReactNode }) {
    const [user, setUser] = useState<MeResponseDto | null>(null)
    const [isAuthLoading, setIsAuthLoading] = useState(true);

    const router = useRouter()

    useEffect(() => {

        if (typeof window !== 'undefined') {
            const token = localStorage.getItem("API_KEY")
            if (token) {
                // Fetch user info from API or decode token
                (new MeApi(getApiConfig())).meControllerMe().then(res => {
                    setUser(res.data)
                }).catch(err => {
                    console.error(err)
                    localStorage.removeItem("API_KEY")
                }).finally(() => {
                    setIsAuthLoading(false); // Done initializing
                })
            } else {
                setIsAuthLoading(false); // Done initializing
            }
        }


    }, [])


    const login = async (email: string, password: string): Promise<JwtResponseDto | undefined> => {
        try {
            const login = await new MeApi(getUnauthenticatedApiConfig()).meControllerLogin({
                email: email,
                password: password
            });
            if (typeof window !== 'undefined') {
                localStorage.setItem("API_KEY", login.data.accessToken);
            }
            const user = await (new MeApi(getApiConfig())).meControllerMe();
            setUser(user.data)
            return login.data;
        } catch (error) {
            console.error(error);
            if (error instanceof AxiosError) {
                throw error.response?.data ?? error;

            }
            throw error;
        }

    }

    const logout = () => {
        setUser(null)
        if (typeof window !== 'undefined') {
            localStorage.removeItem("API_KEY")
            localStorage.removeItem("token")
        }
        router.push("/login")
    }


    return (
        <AuthContext.Provider value={{user, login, logout, isAuthLoading}}>
            {children}
        </AuthContext.Provider>
    )
}

export function useAuth() {
    const ctx = useContext(AuthContext)
    if (!ctx) throw new Error("useAuth must be used within AuthProvider")
    return ctx
}
